import { type RPPDomain } from '@retailer-platform/dashboard/core'
import {
  AdminNavEntryPointV2,
  AdminNavEntryPointV3,
  NavEntryPoint,
  NavEntryPointV3,
} from '@retailer-platform/dashboard/routing'
import { onboardingAdminRouter } from './routing/onboardingAdminRouter'
import { onboardingRouter } from './routing/onboardingRouter'
import { onboardingStoreConfigScopedRouter } from './routing/onboardingStoreConfigScopedRouter.tsx'
import { onboardingPublicRouter } from './routing/onboardingPublicRouter'
import { type DomainMessages } from './utils/domain/intl'
import { routes } from './routing/routes'
import EN_US from './translations/en-US.json'
import { type DomainAccessControlConfig } from './utils/domain/accessControl'
import {
  adminStorefrontSetupViewAccessControl,
  flipDashboardAccessControl,
  mobileManagementViewAccessControl,
  onboardingRetailerProfileViewAccessControl,
  whitelabelSiteCustomizableStringsViewAccessControl,
} from './access-control/onboardingAccess.configuration.ts'
import { Permission } from './access-control/permissions'
import { OnboardingVisibilityWrapper } from './access-control/OnboardingVisibilityWrapper'

const definition: RPPDomain<typeof routes, DomainMessages, DomainAccessControlConfig> = {
  id: 'onboarding',
  totem: {
    // The default IPP tool of all routes in this domain - should match the Navigation Entry in IPP UI.
    // Individual routes can override this by specifying a `tool` prop on their DomainRoute component
    tool: 'onboarding-dashboard-tool',
  },
  routes,
  translations: { EN_US },
  lazyTranslations: {
    ES_ES: () => import('./translations/es-ES.json'),
    FR_FR: () => import('./translations/fr-FR.json'),
  },
  routers: {
    scoped: [onboardingStoreConfigScopedRouter, onboardingRouter],
    admin: [onboardingAdminRouter],
    public: [onboardingPublicRouter],
  },
  navigationEntries: [
    {
      attachToV2: NavEntryPoint.UserMenuSettings,
      subNavItems: [
        {
          type: 'item',
          llmDescription: 'View and edit retailer profile settings during onboarding',
          labelId: 'onboardingDomain.navItem.retailerProfile',
          route: 'onboarding-retailer-settings',
          accessControl: onboardingRetailerProfileViewAccessControl,
          NavItemWrapper: OnboardingVisibilityWrapper,
          positionNavV2: 3,
        },
        {
          type: 'item',
          llmDescription: 'Set up and configure storefront site settings and appearance',
          labelId: 'onboardingDomain.siteSetup.navLabel',
          route: 'onboarding-site-setup',
          accessControl: adminStorefrontSetupViewAccessControl,
          id: 'onboarding-site-setup',
          positionNavV2: 7, // after the 'Storefront Onboarding' options
          subRoutes: ['onboarding-site-setup-review'],
        },
      ],
    },
    {
      attachToV2: NavEntryPoint.OperationsGeneral,
      attachToV3: NavEntryPointV3.Stores,
      subNavItems: [
        {
          type: 'item',
          llmDescription: 'Manage store locations and details during onboarding',
          labelId: 'onboardingDomain.navItem.storeManagement',
          labelIdV3: 'onboardingDomain.navItem.storeManagement.v3',
          route: 'onboarding-store-management',
          subRoutes: ['onboarding-store-detail'],
          accessControl: {
            permissions: [Permission.WarehouseLocationHoursView],
          },
          NavItemWrapper: OnboardingVisibilityWrapper,
          positionNavV2: 1,
          positionNavV3: 1,
        },
      ],
    },
    {
      attachToV2: NavEntryPoint.Merchandising,
      subNavItems: [
        {
          type: 'item',
          llmDescription: 'Customize and manage text strings displayed throughout the storefront',
          labelId: 'onboardingDomain.navItem.stringCustomization',
          route: 'onboarding-string-customizations',
          accessControl: whitelabelSiteCustomizableStringsViewAccessControl,
          isNew: true,
        },
      ],
    },
    {
      attachToV3: NavEntryPointV3.StorefrontPro,
      subNavItems: [
        {
          type: 'item',
          llmDescription: 'Customize and manage text strings displayed throughout the storefront',
          labelId: 'onboardingDomain.navItem.stringCustomization',
          labelIdV3: 'onboardingDomain.navItem.stringCustomization.v3',
          route: 'onboarding-string-customizations-storefront-pro',
          accessControl: whitelabelSiteCustomizableStringsViewAccessControl,
          isNew: true,
          positionNavV3: 18,
        },
      ],
    },
  ],
  adminNavigationEntries: [
    {
      // Site overview is v2-admin only — Figma's v3 Partner settings excludes
      // it. Kept in its own block (no attachToV3) so it cleanly drops out of
      // v3 without per-item filtering downstream.
      attachToV2: AdminNavEntryPointV2.Operations,
      subNavItems: [
        {
          route: 'app-admin-onboarding-site-overview',
          subRoutes: ['app-admin-onboarding-site-setup-admin'],
          labelId: 'onboardingDomain.siteOverview.navLabel',
          accessControl: adminStorefrontSetupViewAccessControl,
          positionNavV2: 10, // The nav entry point might need to be adjusted
        },
      ],
    },
    {
      attachToV2: AdminNavEntryPointV2.Operations,
      attachToV3: AdminNavEntryPointV3.PartnerSettings,
      subNavItems: [
        {
          route: 'app-admin-onboarding-mobile-dashboard',
          subRoutes: ['app-admin-onboarding-mobile-app-detail'],
          labelId: 'onboardingDomain.mobileDashboard.navLabel',
          labelIdV3: 'onboardingDomain.mobileDashboard.navLabel.v3',
          accessControl: mobileManagementViewAccessControl,
          positionNavV2: 11, // The nav entry point might need to be adjusted
          // Group 2 of v3 Partner settings: divider above, then Mobile / FLIP /
          // Entitlements at positions 4–6.
          positionNavV3: 4,
          dividerBefore: true,
        },
        {
          route: 'app-admin-onboarding-flip-dashboard',
          labelId: 'onboardingDomain.flipDashboard.navLabel',
          labelIdV3: 'onboardingDomain.flipDashboard.navLabel.v3',
          accessControl: flipDashboardAccessControl,
          positionNavV2: 12,
          positionNavV3: 5,
          subRoutes: [
            'app-admin-onboarding-flip-site-detail',
            'app-admin-onboarding-flip-feature-detail',
          ],
        },
      ],
    },
  ],
}

export default definition
